/**
 * Native VoIP push bootstrap.
 *
 * On native iOS, registers a listener for `voipPushTokenUpdated` events
 * emitted by ElefaSipPlugin (backed by PKPushRegistry) and uploads the
 * APNs VoIP device token to the backend so Kamailio/edge can target this
 * device with an APNs push when an inbound INVITE arrives.
 *
 * Safe to call multiple times - re-uses a singleton listener handle.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { ElefaSipPlugin, isNativeSipAvailable, type VoipPushTokenEvent } from './elefa-sip-plugin';

let listenerHandle: { remove: () => Promise<void> } | null = null;
let lastUploadedToken: string | null = null;

async function uploadToken(ev: VoipPushTokenEvent): Promise<void> {
  if (!ev.token) {
    // Token invalidated - best-effort remove on backend.
    console.log('[native-voip-push] token invalidated');
    return;
  }
  if (ev.token === lastUploadedToken) return;
  try {
    const { error } = await supabase.functions.invoke('register-voip-push-token', {
      body: {
        token: ev.token,
        platform: ev.platform,
        token_type: ev.tokenType,
      },
    });
    if (error) {
      console.warn('[native-voip-push] upload failed', error);
      return;
    }
    lastUploadedToken = ev.token;
    console.log('[native-voip-push] token uploaded', ev.token.slice(0, 12) + '…');
  } catch (e) {
    console.warn('[native-voip-push] upload threw', e);
  }
}

/** Call once after the user is authenticated on a native device. */
export async function initNativeVoipPush(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !isNativeSipAvailable()) return;
  if (listenerHandle) return;

  listenerHandle = await ElefaSipPlugin.addListener('voipPushTokenUpdated', (ev) => {
    void uploadToken(ev as VoipPushTokenEvent);
  });

  // Fetch any token that was issued before the listener was attached.
  try {
    const cur = await ElefaSipPlugin.getVoipPushToken();
    if (cur?.token) {
      await uploadToken({
        token: cur.token,
        platform: (cur.platform as 'ios' | 'android') ?? 'ios',
        tokenType: (cur.tokenType as 'apns_voip') ?? 'apns_voip',
      });
    }
  } catch (e) {
    console.warn('[native-voip-push] getVoipPushToken failed', e);
  }
}

export async function teardownNativeVoipPush(): Promise<void> {
  if (listenerHandle) {
    await listenerHandle.remove();
    listenerHandle = null;
  }
  lastUploadedToken = null;
}
